import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Award, Lock, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Single Badge Display
 */
const BadgeItem = ({ badge, earned = false, showAnimation = false }) => {
  return (
    <div 
      className={cn(
        "relative flex flex-col items-center p-2 sm:p-3 rounded-xl transition-all",
        earned 
          ? "bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200" 
          : "bg-slate-100 border-2 border-slate-200 opacity-50",
        showAnimation && earned && "animate-bounce"
      )}
      data-testid={`badge-${badge.id || badge.badge_id}`}
    >
      <span className={cn("text-2xl sm:text-3xl mb-1", !earned && "grayscale")}>
        {badge.icon}
      </span>
      <p className={cn(
        "text-[10px] sm:text-xs font-medium text-center leading-tight",
        earned ? "text-amber-800" : "text-slate-500"
      )}>
        {badge.name}
      </p>
      {!earned && (
        <div className="absolute top-1 right-1">
          <Lock className="w-3 h-3 text-slate-400" />
        </div>
      )}
      {earned && (
        <Badge className="absolute -top-1 -right-1 bg-amber-400 text-white text-[10px] px-1">
          ✓
        </Badge>
      )}
    </div>
  );
};

/**
 * Achievements/Badges Section Component
 */
export const AchievementBadges = ({ className }) => {
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBadges, setNewBadges] = useState([]);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      setLoading(true);
      const [earnedRes, allRes] = await Promise.all([
        axios.get(`${API}/badges`),
        axios.get(`${API}/badges/all`)
      ]);
      
      // Check for newly earned badges
      const prevEarnedIds = earnedBadges.map(b => b.badge_id);
      const newlyEarned = earnedRes.data.filter(
        b => !prevEarnedIds.includes(b.badge_id)
      );
      
      if (newlyEarned.length > 0 && earnedBadges.length > 0) {
        setNewBadges(newlyEarned.map(b => b.badge_id));
        newlyEarned.forEach(b => {
          toast.success(`🎉 New Badge Earned: ${b.name}!`);
        });
        // Clear animation after 3 seconds
        setTimeout(() => setNewBadges([]), 3000);
      }
      
      setEarnedBadges(earnedRes.data);
      setAllBadges(allRes.data);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const earnedIds = earnedBadges.map(b => b.badge_id);

  // Group badges by category
  const badgesByCategory = allBadges.reduce((acc, badge) => {
    const cat = badge.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(badge);
    return acc;
  }, {});

  const categoryLabels = {
    milestone: '🌟 Milestones',
    achievement: '🏆 Achievements',
    streak: '📅 Streaks',
    activity: '⚡ Activity'
  };

  return (
    <Card className={cn("shadow-lg rounded-2xl", className)} data-testid="badges-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-500" />
          My Badges
        </CardTitle>
        <CardDescription>
          {earnedBadges.length} of {allBadges.length} badges earned
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
          </div>
        ) : allBadges.length === 0 ? (
          <p className="text-center text-slate-500 py-4">No badges available</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(badgesByCategory).map(([category, badges]) => (
              <div key={category}>
                <p className="text-xs font-medium text-slate-500 mb-2">
                  {categoryLabels[category] || category}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {badges.map((badge) => (
                    <BadgeItem
                      key={badge.id}
                      badge={badge}
                      earned={earnedIds.includes(badge.id)}
                      showAnimation={newBadges.includes(badge.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AchievementBadges;
